// ATLAS — ORACLE renamed, with TELOS folded in (docs/orient/ATLAS_VX_SPEC.md).
// Same shape as ORACLE: spoke toggles + spinning disc on top, sector chips,
// then each S.T.A.G.E. level as one compact, mostly-click card. One store:
// user_settings.oracle_spine via olmps/atlas-data.ts. Edits are local until
// Save; Save writes only changed sectors and syncs Aims to KYRS (aim_ref,
// idempotent — a retry never duplicates tasks).
// ATLAS only sets the S.T.A.G.E. ladder; alignment scores live on Home, not here (Joel, 18 Oct 2026).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import type { ToolProps } from '../olmps/ToolHost';
import { useCompassConfig, builtinSpokes } from '../olmps/sectorConfig';
import { registerDeityForm, unregisterDeityForm } from '../olmps/deityFormRegistry';
import {
  loadAtlas,
  loadAtlasEvidence,
  saveAtlasSectors,
  syncAimTasks,
  currentOf,
  todayKey,
  PRESET_ENDEAVOURS,
  SECTOR_DEITY_MAP,
  type AtlasEvidence,
  type AtlasNode,
  type AtlasSpine,
} from '../olmps/atlas-data';
import { effectiveAims, getActivityPresetsForDeity, type OracleAim } from '../olmps/oracle-spine-data';
import { useOnboarding, useTourTarget, tourBus } from '../v10/onboarding';
import { Atmos, Chip, Field, GlassCard, GoldButton } from '../v10/ui';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR } from '../v10/theme';
import { AtlasDisc } from './atlas/AtlasDisc';
import { AtlasSurvey } from './atlas/AtlasSurvey';
import { AtlasDropdown } from './atlas/AtlasDropdown';
import { AtlasStageTable } from './atlas/AtlasStageTable';
import { ScreenHelp } from '../v10/ScreenHelp';
import { SHAPE_DEITIES } from '../v10/shapeRoster';
import { atlasHelp } from '../olmps/help/atlasHelp';
import {
  aimsFor,
  currentDeity,
  deityForEndeavour,
  deityNameOf,
  endeavoursFor,
  fillFromAims,
  fillFromEndeavour,
  fillFromGoal,
  findAim,
  findGoal,
  goalsFor,
  principlesFor,
  stepsFor,
  todaysFor,
  toOracleAim,
  treeAimId,
  type Fill,
} from '../olmps/atlas-tree';
import { FOUND, fetchAtlasDeity, fetchAtlasOptions, fetchAtlasPrinciples, genKey, type GenLevel, type GenPath } from '../olmps/atlas-tree/aiOptions';

const SURVEY = '__survey__';

/** Options as {key,label}, keeping the current value(s) visible even when custom. */
function withCurrent(list: string[], current: string | null | undefined, extra: string[] = []) {
  const out = [...list];
  for (const v of [current, ...extra]) if (v && !out.includes(v)) out.push(v);
  return out.map((v) => ({ key: v, label: v }));
}

/** An AI-written Aim option as a saved Aim (same id scheme as tree aims). */
const genAim = (t: string): OracleAim => ({ id: treeAimId({ t }), label: t, cadence: 'weekly' });

interface GenNeed {
  level: GenLevel;
  parent: string;
  path: GenPath;
}

/** Levels whose parent is a custom entry the tree has no options for, and no AI options cached yet. */
function genNeeds(s: Sector, n: AtlasNode): GenNeed[] {
  const gen = n.atlas.gen ?? {};
  const out: GenNeed[] = [];
  const add = (level: GenLevel, parent: string, path: GenPath) => {
    if (parent && !gen[genKey(level, parent)]) out.push({ level, parent, path });
  };
  const e = n.endeavour ?? '';
  const goal = n.atlas.goal.text;
  if (e && goalsFor(s, e).length === 0) {
    add('deity', e, { endeavour: e });
    add('goal', e, { endeavour: e });
  }
  if (goal && !findGoal(s, e, goal)) add('aim', goal, { endeavour: e, goal });
  for (const a of n.aims ?? []) {
    if (!findAim(s, a.label)?.d.length) add('today', a.label, { endeavour: e, goal, aim: a.label });
  }
  const today = currentOf(n.atlas.today)?.text ?? '';
  const labels = (n.aims ?? []).map((a) => a.label);
  if (today && stepsFor(s, labels, today).length === 0) {
    const aim = labels.find((l) => (gen[genKey('today', l)] ?? []).includes(today)) ?? labels[0] ?? '';
    add('step', today, { endeavour: e, goal, aim, today });
  }
  // Principles for a custom Goal, written once its aims exist (they are the "what I'm doing").
  if (goal && !findGoal(s, e, goal) && labels.length) add('principle', goal, { endeavour: e, goal, aim: labels.join('; '), today });
  return out;
}

/** #rrggbb at an alpha, for the unselected sector segments' colour wash. */
const wash = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/** Tour target wrapper for a sector segment (the onboarding tour points at it). */
function TourBox({ id, children, style }: { id: string; children: React.ReactNode; style?: object }) {
  const tt = useTourTarget(id);
  return <View ref={tt.onLayoutRef} collapsable={false} style={style}>{children}</View>;
}

/** Survives a remount (layout swap, hot reload, data refresh): the sector last looked at and any
 *  unsaved edits, so ATLAS never jumps back to the host's default sector mid-edit. */
let draft: { userId: string; spine: AtlasSpine; saved: AtlasSpine; dirty: Sector[] } | null = null;
let lastSector: Sector | null = null;

export function AtlasTool({ sector: initialSector }: ToolProps) {
  const styles = useStyles();
  const ob = useOnboarding();
  const { t } = useTheme();
  const { session } = useAuth();
  const userId = session?.user.id;

  // A draft with unsaved edits from before a remount is picked back up instead of reloading.
  const [restored] = useState(() => (draft && draft.userId === userId && draft.dirty.length ? draft : null));
  const [spine, setSpine] = useState<AtlasSpine | null>(restored?.spine ?? null);
  const [saved, setSaved] = useState<AtlasSpine | null>(restored?.saved ?? null);
  const [dirty, setDirty] = useState<Set<Sector>>(() => new Set(restored?.dirty ?? []));
  const [evidence, setEvidence] = useState<AtlasEvidence | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const system = 'shape' as const;
  const [newSpoke, setNewSpoke] = useState('');
  const [adding, setAdding] = useState(false);
  /** Add the typed sector (if any) and close the name field. */
  const addSpoke = () => {
    if (newSpoke.trim()) addCustom(system, newSpoke.trim());
    setNewSpoke('');
    setAdding(false);
  };
  const [survey, setSurvey] = useState(false);
  const [dd, setDd] = useState<string | null>(null);

  const { config, spokesFor, isEnabled, toggleBuiltin, addCustom, renameSpoke, removeCustom } = useCompassConfig(userId);
  const disabled = config.systems.shape.disabled;
  const enabledSectors = useMemo(() => SECTORS.filter((s) => !disabled.includes(s)), [disabled]);
  const [selected, setSelected] = useState<Sector>(lastSector ?? (initialSector && SECTORS.includes(initialSector) ? initialSector : 'S'));
  useEffect(() => {
    lastSector = selected;
  }, [selected]);
  useEffect(() => {
    if (userId && spine && saved) draft = { userId, spine, saved, dirty: [...dirty] };
  }, [userId, spine, saved, dirty]);
  const shapeLabel = (s: Sector) => config.systems.shape.labels?.[s] || builtinSpokes('shape').find((b) => b.id === s)?.label || s;

  useEffect(() => {
    if (disabled.includes(selected) && enabledSectors[0]) setSelected(enabledSectors[0]);
  }, [disabled, selected, enabledSectors]);
  useEffect(() => {
    setSurvey(false);
    setDd(null);
  }, [selected]);
  // Tour: a wait that is already satisfied (chip already selected / picker already open) fires at once.
  useEffect(() => {
    if (!ob.active) return;
    if (ob.liveTarget === `atlas.chip.${SECTOR_LABELS[selected].toLowerCase()}`) tourBus.emit(`atlas:chip:${SECTOR_LABELS[selected].toLowerCase()}`);
    else if (ob.liveTarget === 'atlas.endeavour' && dd === 'E') tourBus.emit('atlas:endeavour:open');
  }, [ob.active, ob.liveTarget, selected, dd]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoadError(null);
    try {
      const s = await loadAtlas(supabase, userId);
      setSpine(s);
      setSaved(s);
      setDirty(new Set(SECTORS.filter((sec) => s[sec].isDirty)));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load ATLAS');
    }
    loadAtlasEvidence(supabase, userId).then(setEvidence).catch(() => setEvidence(null));
  }, [userId]);

  useEffect(() => {
    if (restored) {
      if (userId) loadAtlasEvidence(supabase, userId).then(setEvidence).catch(() => setEvidence(null));
      return;
    }
    void load();
  }, [load, restored, userId]);

  const patch = useCallback((sector: Sector, fn: (n: AtlasNode) => AtlasNode) => {
    setSpine((s) => (s ? { ...s, [sector]: fn(s[sector]) } : s));
    setDirty((d) => new Set(d).add(sector));
    setSaveMsg(null);
  }, []);

  const save = useCallback(async () => {
    if (!userId || !spine || !saved) return;
    const sectors = SECTORS.filter((s) => dirty.has(s) || spine[s].atlas.imported.length > 0);
    if (!sectors.length) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await saveAtlasSectors(supabase, userId, spine, sectors);
      for (const s of sectors) await syncAimTasks(supabase, userId, s, effectiveAims(saved[s]), effectiveAims(spine[s]));
      const clean = { ...spine };
      for (const s of sectors) clean[s] = { ...clean[s], isDirty: false, atlas: { ...clean[s].atlas, imported: [] } };
      setSpine(clean);
      setSaved(clean);
      setDirty(new Set());
      setSaveMsg({ ok: true, text: 'Saved' });
      tourBus.emit('atlas:saved');
      loadAtlasEvidence(supabase, userId).then(setEvidence).catch(() => {});
    } catch (e) {
      setSaveMsg({ ok: false, text: e instanceof Error ? e.message : 'Save failed' });
    } finally {
      setSaving(false);
    }
  }, [userId, spine, saved, dirty]);

  // Deity agents read / fill the selected sector; fills land as unsaved edits.
  const stateRef = useRef({ spine, selected });
  stateRef.current = { spine, selected };
  useEffect(() => {
    registerDeityForm('atlas', {
      getCurrentValues: () => {
        const { spine: s, selected: sec } = stateRef.current;
        if (!s) return undefined;
        const n = s[sec];
        return {
          sector: sec,
          endeavour: n.endeavour,
          deity: n.goal,
          goal: n.atlas.goal.text,
          aims: effectiveAims(n).map((a) => a.label),
          today: currentOf(n.atlas.today)?.text ?? null,
          step: currentOf(n.atlas.step)?.text ?? null,
        };
      },
      applyFill: (fields) => {
        const sec = stateRef.current.selected;
        const str = (k: string) => (typeof fields[k] === 'string' ? (fields[k] as string) : undefined);
        patch(sec, (n) => {
          const a = n.atlas;
          const day = todayKey();
          return {
            ...n,
            endeavour: str('endeavour') ?? n.endeavour,
            endeavourSource: str('endeavour') ? 'manual' : n.endeavourSource,
            atlas: {
              ...a,
              goal: { ...a.goal, text: str('goal') ?? a.goal.text },
              today: str('today') ? { text: str('today')!, why: '', arrival: '', date: day } : a.today,
              step: str('step') ? { text: str('step')!, why: '', arrival: '', date: day } : a.step,
            },
          };
        });
      },
    });
    return () => unregisterDeityForm('atlas');
  }, [patch]);

  // Custom entries: the level below gets 10 AI-written options (cached in atlas.gen). When that
  // level is still empty its first option is picked, so the chain fills down like the tree does.
  const [genBusy, setGenBusy] = useState<Set<string>>(new Set());
  const [genFailed, setGenFailed] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!spine) return;
    const s = selected;
    for (const need of genNeeds(s, spine[s])) {
      const k = `${s}:${genKey(need.level, need.parent)}`;
      if (genBusy.has(k) || genFailed.has(k)) continue;
      setGenBusy((b) => new Set(b).add(k));
      if (need.level === 'deity') {
        // A custom Endeavour trains the closest deity: the keyword match shows at once, the AI's pick replaces it.
        fetchAtlasDeity(s, need.parent, SHAPE_DEITIES.filter((d) => d.sector === s))
          .then((id) => {
            if (!id) throw new Error('no deity');
            patch(s, (n) => ({
              ...n,
              goal: n.endeavour === need.parent ? id : n.goal,
              atlas: { ...n.atlas, gen: { ...(n.atlas.gen ?? {}), [genKey('deity', need.parent)]: [id] } },
            }));
          })
          .catch(() => setGenFailed((f) => new Set(f).add(k)))
          .finally(() => setGenBusy((b) => { const nb = new Set(b); nb.delete(k); return nb; }));
        continue;
      }
      if (need.level === 'principle') {
        fetchAtlasPrinciples(s, need.parent, need.path)
          .then(({ formula, found }) => {
            if (!formula.length && !found.length) throw new Error('no principles');
            patch(s, (n) => {
              const gen = { ...(n.atlas.gen ?? {}), [genKey('principle', need.parent)]: formula, [`${FOUND}|${need.parent}`]: found };
              const pick = !n.atlas.endeavour.principles.length && n.atlas.goal.text === need.parent;
              return {
                ...n,
                atlas: {
                  ...n.atlas,
                  gen,
                  endeavour: pick ? { ...n.atlas.endeavour, principles: [formula[0] ?? found[0]] } : n.atlas.endeavour,
                },
              };
            });
          })
          .catch(() => setGenFailed((f) => new Set(f).add(k)))
          .finally(() => setGenBusy((b) => { const nb = new Set(b); nb.delete(k); return nb; }));
        continue;
      }
      fetchAtlasOptions(s, need.level, need.parent, need.path)
        .then((options) => {
          if (!options.length) throw new Error('no options');
          patch(s, (n) => {
            const day = todayKey();
            const a = { ...n.atlas, gen: { ...(n.atlas.gen ?? {}), [genKey(need.level, need.parent)]: options } };
            const next: AtlasNode = { ...n, atlas: a };
            const first = options[0];
            const dated = (text: string) => ({ why: '', arrival: '', text, date: day });
            if (need.level === 'goal' && n.endeavour === need.parent && !a.goal.text) {
              return { ...next, aims: null, atlas: { ...a, goal: { ...a.goal, text: first }, today: dated(''), step: dated('') } };
            }
            if (need.level === 'aim' && a.goal.text === need.parent && !(n.aims ?? []).length) {
              return { ...next, aims: [genAim(first)], atlas: { ...a, today: dated(''), step: dated('') } };
            }
            if (need.level === 'today' && (n.aims ?? []).some((x) => x.label === need.parent) && !currentOf(a.today)?.text) {
              return { ...next, atlas: { ...a, today: dated(first), step: dated('') } };
            }
            if (need.level === 'step' && currentOf(a.today)?.text === need.parent && !currentOf(a.step)?.text) {
              return { ...next, atlas: { ...a, step: dated(first) } };
            }
            return next;
          });
        })
        .catch(() => setGenFailed((f) => new Set(f).add(k)))
        .finally(() => setGenBusy((b) => { const nb = new Set(b); nb.delete(k); return nb; }));
    }
  }, [spine, selected, genBusy, genFailed, patch]);

  if (!userId) return <Atmos><View style={styles.center}><Text style={styles.muted}>Sign in to use ATLAS.</Text></View></Atmos>;
  if (loadError) {
    return (
      <Atmos>
        <View style={styles.center}>
          <Text style={styles.error}>{loadError}</Text>
          <GoldButton compact label="Retry" onPress={() => void load()} style={{ marginTop: 12 }} />
        </View>
      </Atmos>
    );
  }
  if (!spine) return <Atmos><View style={styles.center}><ActivityIndicator color={t.gold} /></View></Atmos>;

  const node = spine[selected];
  const color = SECTOR_COLOR[selected];
  const name = shapeLabel(selected);
  const deities = SECTOR_DEITY_MAP[selected] ?? [];
  const withDeity = (text: string, deityId: string | null) => {
    const d = deityNameOf(selected, deityId);
    return d ? `${text} (${d})` : text;
  };
  const aims = node.aims ?? [];
  const deityId = currentDeity(selected, node.goal, node.endeavour);
  const aimPresets = getActivityPresetsForDeity(deityId);
  const today = currentOf(node.atlas.today)?.text ?? '';
  const step = currentOf(node.atlas.step)?.text ?? '';
  const nextTask = evidence?.[selected].nextOpen?.title ?? null;
  const pending = dirty.size > 0 || SECTORS.some((s) => spine[s].atlas.imported.length > 0);

  const goalText = node.atlas.goal.text;
  const aimLabels = aims.map((a) => a.label);
  const principles = node.atlas.endeavour.principles;
  const treeGoal = findGoal(selected, node.endeavour, goalText);
  // AI-written options for levels under a custom entry (see genNeeds).
  const gen = node.atlas.gen ?? {};
  const genOf = (level: GenLevel, parent: string | null | undefined) => (parent ? gen[genKey(level, parent)] ?? [] : []);
  const writing = (level: GenLevel) => [...genBusy].some((k) => k.startsWith(`${selected}:${level}|`));
  const failed = (level: GenLevel) => [...genFailed].some((k) => k.startsWith(`${selected}:${level}|`));
  /** Placeholder for a row whose options are AI-written: writing, failed (opening the row retries), or idle. */
  const genPlaceholder = (level: GenLevel, idle: string) =>
    writing(level) ? `Writing ${level} options…` : failed(level) ? `Couldn't write ${level} options — tap to retry` : idle;
  /** Opening a row retries its failed AI options. */
  const retryGen = (rowKey: string | null) => {
    const level = ({ G: 'goal', A: 'aim', T: 'today', S: 'step', P: 'principle' } as Record<string, GenLevel>)[rowKey ?? ''];
    if (level && failed(level)) setGenFailed((f) => new Set([...f].filter((k) => !k.startsWith(`${selected}:${level}|`))));
  };
  const goalOptions = [...goalsFor(selected, node.endeavour).map((g) => g.t), ...genOf('goal', node.endeavour)];
  // Aim options: once the AI has written aims for a custom goal, only those; otherwise the goal's tree
  // aims + TELOS habits. Anything already chosen stays listed (custom / older presets).
  const genAims = treeGoal ? [] : genOf('aim', goalText).map(genAim);
  const aimOptions: OracleAim[] = (genAims.length ? genAims : [...aimsFor(selected, treeGoal).map(toOracleAim), ...aimPresets])
    .filter((a, i, arr) => arr.findIndex((x) => x.id === a.id) === i);
  for (const a of aims) if (!aimOptions.some((x) => x.id === a.id)) aimOptions.push(a);
  const todayOptions = [...todaysFor(selected, aimLabels).map((d) => d.t), ...aimLabels.flatMap((l) => genOf('today', l))]
    .filter((v, i, arr) => arr.indexOf(v) === i);
  const stepsOf = (d: string) => {
    const tree = stepsFor(selected, aimLabels, d);
    return tree.length ? tree : genOf('step', d);
  };
  // Steps for the chosen Today; before Today is picked (it resets each day), every Today's steps.
  const stepOptions = (today ? stepsOf(today) : todayOptions.flatMap(stepsOf)).filter((v, i, arr) => arr.indexOf(v) === i);

  /** Apply an auto-fill to the lower levels of a node. */
  const fill = (n: AtlasNode, f: Fill): AtlasNode => {
    const day = todayKey();
    const a = n.atlas;
    return {
      ...n,
      aims: f.aims !== undefined ? (f.aims.length ? f.aims : null) : n.aims,
      atlas: {
        ...a,
        goal: f.goal !== undefined ? { ...a.goal, text: f.goal } : a.goal,
        today: f.today !== undefined ? { why: '', arrival: '', ...(currentOf(a.today) ?? {}), text: f.today, date: day } : a.today,
        step: f.step !== undefined ? { why: '', arrival: '', ...(currentOf(a.step) ?? {}), text: f.step, date: day } : a.step,
        endeavour: f.principles !== undefined ? { ...a.endeavour, principles: f.principles } : a.endeavour,
      },
    };
  };
  const pickEndeavour = (text: string, source: 'manual' | 'survey') => {
    patch(selected, (n) =>
      fill(
        {
          ...n,
          endeavour: text || null,
          endeavourSource: text ? source : null,
          goal: (text ? n.atlas.gen?.[genKey('deity', text)]?.[0] : undefined) ?? deityForEndeavour(selected, text) ?? n.goal,
        },
        fillFromEndeavour(selected, text),
      ),
    );
    if (text) tourBus.emit(`atlas:endeavour:${SECTOR_LABELS[selected].toLowerCase()}`);
  };
  const pickGoal = (text: string) =>
    patch(selected, (n) => {
      const tree = findGoal(selected, n.endeavour, text);
      // A custom Goal gets its own principles: the first written one if already cached, else none until written.
      const own = n.atlas.gen?.[genKey('principle', text)]?.[0];
      return fill(n, { goal: text, ...fillFromGoal(tree), ...(tree ? {} : { principles: own ? [own] : [] }) });
    });
  const toggleAim = (a: OracleAim) =>
    patch(selected, (n) => {
      const cur = n.aims ?? [];
      const next = cur.some((x) => x.id === a.id) ? cur.filter((x) => x.id !== a.id) : [...cur, a];
      const tree = next.map((x) => findAim(selected, x.label)).filter((x): x is NonNullable<typeof x> => !!x);
      const f = fillFromAims(tree);
      // Custom aims have no tree principle: keep the principles already chosen.
      if (!tree.length) delete f.principles;
      return fill(n, { aims: next, ...f });
    });
  const pickToday = (text: string) =>
    patch(selected, (n) =>
      fill(n, { today: text, step: stepsFor(selected, (n.aims ?? []).map((x) => x.label), text)[0] ?? n.atlas.gen?.[genKey('step', text)]?.[0] ?? '' }),
    );
  const setDay = (kind: 'today' | 'step', text: string) =>
    patch(selected, (n) => fill(n, kind === 'today' ? { today: text } : { step: text }));
  const setPrinciples = (next: string[]) => patch(selected, (n) => fill(n, { principles: next }));

  // ── Edit / delete custom options (typed or AI-written; presets stay fixed) ──
  /** atlas.gen with `old` renamed to `next` (null = removed) in every list of `level`, and the
   *  list written under it (genKey(child, old)) moved to the new text, or dropped on delete. */
  const editGen = (g: Record<string, string[]>, level: GenLevel | null, child: GenLevel | null, old: string, next: string | null) => {
    const out: Record<string, string[]> = {};
    for (const [k, list] of Object.entries(g)) {
      let key = k;
      let l = list;
      if (level && k.startsWith(`${level}|`)) l = next ? list.map((x) => (x === old ? next : x)) : list.filter((x) => x !== old);
      if (child && k === genKey(child, old)) {
        if (!next) continue;
        key = genKey(child, next);
      }
      out[key] = l;
    }
    return out;
  };
  const withGen = (n: AtlasNode, level: GenLevel | null, child: GenLevel | null, old: string, next: string | null): AtlasNode => ({
    ...n,
    atlas: { ...n.atlas, gen: editGen(n.atlas.gen ?? {}, level, child, old, next) },
  });
  const renameDated = (t: AtlasNode['atlas']['today'], old: string, next: string) => (t && t.text === old ? { ...t, text: next } : t);
  const renameOption = (row: 'E' | 'G' | 'A' | 'T' | 'S', key: string, label: string) =>
    patch(selected, (n0) => {
      if (row === 'E') {
        const n = withGen(n0, null, 'goal', key, label);
        return n.endeavour === key ? { ...n, endeavour: label } : n;
      }
      if (row === 'G') {
        const n = withGen(n0, 'goal', 'aim', key, label);
        return n.atlas.goal.text === key ? { ...n, atlas: { ...n.atlas, goal: { ...n.atlas.goal, text: label } } } : n;
      }
      if (row === 'A') {
        const old = aimOptions.find((a) => a.id === key)?.label ?? '';
        const n = withGen(n0, 'aim', 'today', old, label);
        const aimsNext = (n.aims ?? []).map((a) =>
          a.id === key ? { ...a, label, id: a.id === genAim(old).id ? genAim(label).id : a.id } : a,
        );
        return { ...n, aims: aimsNext.length ? aimsNext : n.aims };
      }
      if (row === 'T') {
        const n = withGen(n0, 'today', 'step', key, label);
        return { ...n, atlas: { ...n.atlas, today: renameDated(n.atlas.today, key, label) } };
      }
      const n = withGen(n0, 'step', null, key, label);
      return { ...n, atlas: { ...n.atlas, step: renameDated(n.atlas.step, key, label) } };
    });
  const deleteOption = (row: 'E' | 'G' | 'A' | 'T' | 'S', key: string) =>
    patch(selected, (n0) => {
      if (row === 'E') {
        const n = withGen(n0, null, 'goal', key, null);
        return n.endeavour === key ? fill({ ...n, endeavour: null, endeavourSource: null }, { goal: '', aims: [], today: '', step: '' }) : n;
      }
      if (row === 'G') {
        const n = withGen(n0, 'goal', 'aim', key, null);
        return n.atlas.goal.text === key ? fill(n, { goal: '', aims: [], today: '', step: '' }) : n;
      }
      if (row === 'A') {
        const old = aimOptions.find((a) => a.id === key)?.label ?? '';
        const n = withGen(n0, 'aim', 'today', old, null);
        const left = (n.aims ?? []).filter((a) => a.id !== key);
        return left.length === (n.aims ?? []).length ? n : fill(n, { aims: left, ...(left.length ? {} : { today: '', step: '' }) });
      }
      if (row === 'T') {
        const n = withGen(n0, 'today', 'step', key, null);
        return currentOf(n.atlas.today)?.text === key ? fill(n, { today: '', step: '' }) : n;
      }
      const n = withGen(n0, 'step', null, key, null);
      return currentOf(n.atlas.step)?.text === key ? fill(n, { step: '' }) : n;
    });
  // Presets per level: everything else in a list is custom and gets rename + delete.
  const presetEndeavours = endeavoursFor(selected);
  const presetGoals = goalsFor(selected, node.endeavour).map((g) => g.t);
  const presetAimIds = new Set([...aimsFor(selected, treeGoal).map(toOracleAim), ...aimPresets].map((a) => a.id));
  const presetTodays = todaysFor(selected, aimLabels).map((d) => d.t);
  const presetSteps = [
    ...(today ? stepsFor(selected, aimLabels, today) : todaysFor(selected, aimLabels).flatMap((d) => d.s)),
    ...(nextTask ? [nextTask] : []),
  ];
  // Principles: for a custom Goal, the AI's — written for this ladder (yellow) then found ones (green);
  // otherwise the tree / sector principles. Chosen ones always stay listed.
  const genFormula = treeGoal ? [] : genOf('principle', goalText);
  const genFound = treeGoal || !goalText ? [] : gen[`${FOUND}|${goalText}`] ?? [];
  const principleOptions = (genFormula.length || genFound.length
    ? withCurrent([...genFormula, ...genFound], null, principles)
    : withCurrent(principlesFor(selected, aimLabels), null, principles)
  ).map((o) => ({ ...o, color: genFormula.includes(o.key) ? t.gold : genFound.includes(o.key) ? t.onPace : undefined }));
  const mark = (preset: string[]) => (o: { key: string; label: string }) => ({ ...o, editable: !preset.includes(o.key) });

  return (
    <Atmos>
      {/* Bottom room only while the floating Save bar is showing. */}
      <ScrollView contentContainerStyle={[styles.scroll, { paddingBottom: pending || saveMsg ? 120 : 16 }]} keyboardShouldPersistTaps="handled">
        {/* "?" — explains the whole screen. */}
        <View style={styles.helpRow}>
          <ScreenHelp help={atlasHelp({ written: t.gold, found: t.onPace })} />
        </View>
        {/* SHAPE spokes: disc, toggles, rename, add */}
        <GlassCard style={styles.panel}>
          <View style={styles.panelBody}>
            <View style={styles.discWrap}>
              <AtlasDisc spokes={spokesFor('shape')} spine={spine} selected={selected} onSelect={setSelected} />
            </View>
            <View style={styles.spokes}>
              {/* Add sector: just the button; pressing it opens a name field to its left. */}
              <View style={styles.addRow}>
                {adding ? (
                  <Field
                    style={{ flex: 1 }}
                    placeholder="Sector name"
                    value={newSpoke}
                    onChangeText={setNewSpoke}
                    maxLength={24}
                    autoFocus
                    onSubmitEditing={addSpoke}
                  />
                ) : (
                  <Text style={[styles.heading, { flex: 1 }]}>SHAPE SECTORS</Text>
                )}
                <Chip small label={adding && !newSpoke.trim() ? 'Cancel' : '+ Add'} onPress={adding ? addSpoke : () => setAdding(true)} />
              </View>
              {builtinSpokes(system).map((b) => (
                <View key={b.id} style={styles.spokeRow}>
                  <View style={[styles.swatch, { backgroundColor: b.color }]} />
                  <SpokeName value={config.systems[system].labels?.[b.id] || b.label} onCommit={(v) => renameSpoke(system, b.id, v === b.label ? '' : v)} />
                  <Switch
                    value={isEnabled(system, b.id)}
                    onValueChange={() => toggleBuiltin(system, b.id)}
                    trackColor={{ true: t.gold, false: t.track }}
                  />
                </View>
              ))}
              {config.systems[system].custom.map((cs) => (
                <View key={cs.id} style={styles.spokeRow}>
                  <View style={[styles.swatch, { backgroundColor: cs.color }]} />
                  <SpokeName value={cs.label} onCommit={(v) => v && renameSpoke(system, cs.id, v)} />
                  <Pressable onPress={() => removeCustom(system, cs.id)} hitSlop={8} accessibilityLabel={`Remove ${cs.label}`}>
                    <Text style={styles.remove}>✕</Text>
                  </Pressable>
                </View>
              ))}
            </View>
          </View>
        </GlassCard>

        {/* Sector: the chips joined into one long pill — same chip look, a segment per sector,
            the selected one filled with its sector colour. */}
        <View style={styles.segBar}>
          {enabledSectors.map((s, i) => {
            const on = s === selected;
            const label = shapeLabel(s);
            return (
              <TourBox key={s} id={`atlas.chip.${SECTOR_LABELS[s].toLowerCase()}`} style={[styles.segBox, i > 0 && styles.segDivider]}>
                <Pressable
                  style={({ pressed }) => [
                    styles.seg,
                    on ? ({ backgroundColor: SECTOR_COLOR[s], boxShadow: `0 0 12px ${t.goldGlow}` } as object) : { backgroundColor: wash(SECTOR_COLOR[s], 0.22) },
                    pressed && { opacity: 0.8 },
                  ]}
                  onPress={() => { setSelected(s); tourBus.emit(`atlas:chip:${SECTOR_LABELS[s].toLowerCase()}`); }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: on }}
                  accessibilityLabel={label}
                >
                  <Text style={[styles.segText, { color: on ? t.onGold : t.ivorySoft }]}>{shapeLabel(s)}</Text>
                </Pressable>
              </TourBox>
            );
          })}
        </View>

        {/* S.T.A.G.E. — one connected block; each row is a dropdown pre-filled from the row above. */}
        <Label color={color}>{name}</Label>
        {/* TODO(§12.4): SHAPE deity action dropdown for this sector goes here once the action list is decided. */}
        {survey ? (
          <GlassCard style={styles.card}>
            <AtlasSurvey userId={userId} sector={selected} onCancel={() => setSurvey(false)} onPick={(e) => { pickEndeavour(e, 'survey'); setSurvey(false); }} />
          </GlassCard>
        ) : (
          <AtlasStageTable
            accent={color}
            open={dd}
            setOpen={(k) => { setDd(k); retryGen(k); if (k === 'E') tourBus.emit('atlas:endeavour:open'); }}
            rows={[
              {
                key: 'E',
                tourId: 'atlas.endeavour',
                menuTourId: 'atlas.endeavour.options',
                label: 'Endeavour',
                value: node.endeavour ? withDeity(node.endeavour, deityId) : null,
                placeholder: 'Select endeavour…',
                options: [
                  { key: SURVEY, label: 'Not sure? Take the survey →' },
                  ...withCurrent(endeavoursFor(selected), node.endeavour).map((o) => ({
                    key: o.key,
                    label: withDeity(o.key, o.key === node.endeavour ? deityId : deityForEndeavour(selected, o.key)),
                    editable: !presetEndeavours.includes(o.key),
                    editText: o.key,
                  })),
                ],
                selected: node.endeavour ? [node.endeavour] : [],
                onPick: (k) => (k === SURVEY ? setSurvey(true) : pickEndeavour(k, 'manual')),
                onAddCustom: (v) => pickEndeavour(v, 'manual'),
                customPlaceholder: 'Custom endeavour…',
                onEdit: (k, v) => renameOption('E', k, v),
                onDelete: (k) => deleteOption('E', k),
              },
              {
                key: 'G',
                label: 'Goal',
                value: goalText || null,
                placeholder: genPlaceholder('goal', 'Select goal…'),
                options: withCurrent(goalOptions, goalText).map(mark(presetGoals)),
                selected: goalText ? [goalText] : [],
                onPick: pickGoal,
                onAddCustom: pickGoal,
                customPlaceholder: 'Custom goal…',
                onEdit: (k, v) => renameOption('G', k, v),
                onDelete: (k) => deleteOption('G', k),
              },
              {
                key: 'A',
                label: 'Aim',
                value: aims.length === 0 ? null : aims.length === 1 ? aims[0].label : `${aims[0].label} +${aims.length - 1}`,
                placeholder: genPlaceholder('aim', 'Select aims…'),
                multi: true,
                options: aimOptions.map((a) => ({ key: a.id, label: a.label, editable: !presetAimIds.has(a.id) })),
                selected: aims.map((a) => a.id),
                onPick: (k) => toggleAim(aimOptions.find((a) => a.id === k)!),
                onAddCustom: (v) => toggleAim({ id: `custom-${Date.now()}`, label: v, cadence: 'weekly' }),
                customPlaceholder: 'Custom aim…',
                onEdit: (k, v) => renameOption('A', k, v),
                onDelete: (k) => deleteOption('A', k),
              },
              {
                key: 'T',
                label: 'Today',
                value: today || null,
                placeholder: genPlaceholder('today', 'Select today…'),
                options: withCurrent(todayOptions, today).map(mark(presetTodays)),
                selected: today ? [today] : [],
                onPick: pickToday,
                onAddCustom: pickToday,
                customPlaceholder: 'Custom today…',
                onEdit: (k, v) => renameOption('T', k, v),
                onDelete: (k) => deleteOption('T', k),
              },
              {
                key: 'S',
                label: 'Step',
                value: step || null,
                placeholder: genPlaceholder('step', 'Select step…'),
                options: withCurrent([...stepOptions, ...(nextTask ? [nextTask] : [])], step).map(mark(presetSteps)),
                selected: step ? [step] : [],
                onPick: (k) => setDay('step', k),
                onAddCustom: (v) => setDay('step', v),
                customPlaceholder: 'Custom step…',
                onEdit: (k, v) => renameOption('S', k, v),
                onDelete: (k) => deleteOption('S', k),
              },
            ]}
          />
        )}

        <Label color={color}>Principles</Label>
        <AtlasDropdown
          multi
          placeholder={genPlaceholder('principle', 'Select principles…')}
          options={principleOptions}
          selected={principles}
          open={dd === 'P'}
          onOpen={(o) => { setDd(o ? 'P' : null); if (o) retryGen('P'); }}
          onPick={(k) => setPrinciples(principles.includes(k) ? principles.filter((x) => x !== k) : [...principles, k])}
          onAddCustom={(v) => !principles.includes(v) && setPrinciples([...principles, v])}
          customPlaceholder="Custom principle…"
        />
      </ScrollView>

      {pending || saveMsg ? (
        <View style={styles.saveBar}>
          {saveMsg ? <Text style={[styles.msg, { color: saveMsg.ok ? t.onPace : t.lapsed }]} numberOfLines={2}>{saveMsg.text}</Text> : null}
          {pending ? <TourBox id="atlas.save" style={{ flex: 1 }}><GoldButton label={saving ? 'Saving…' : 'Save'} disabled={saving} onPress={() => void save()} /></TourBox> : null}
        </View>
      ) : null}
    </Atmos>
  );
}

function Label({ children, color }: { children: React.ReactNode; color: string }) {
  const styles = useStyles();
  return <Text style={[styles.label, { color }]}>{children}</Text>;
}

/** Tap-to-rename spoke name; commits on blur / enter. */
function SpokeName({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const styles = useStyles();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const v = draft.trim();
    if (v !== value) onCommit(v);
  };
  return <TextInput style={styles.spokeName} value={draft} onChangeText={setDraft} onBlur={commit} onSubmitEditing={commit} maxLength={24} />;
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
    muted: { fontSize: 13, color: t.ivorySoft },
    error: { fontSize: 14, color: t.lapsed, textAlign: 'center' },
    scroll: { padding: 16, paddingBottom: 120, gap: 10 },
    panel: { padding: 14, gap: 10 },
    tabs: { flexDirection: 'row', gap: 8 },
    panelBody: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 8 },
    discWrap: { alignItems: 'center' },
    spokes: { flexGrow: 1, flexBasis: 260, gap: 6 },
    heading: { fontFamily: DISPLAY_FONT, fontSize: 13, letterSpacing: 1, color: t.gold },
    spokeRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    swatch: { width: 16, height: 16, borderRadius: 4 },
    spokeName: { flex: 1, fontSize: 15, fontWeight: '700', color: t.ivory, paddingVertical: 4 },
    remove: { fontSize: 15, color: t.ivorySoft, paddingHorizontal: 6 },
    addRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },
    segBar: { flexDirection: 'row', borderWidth: 1, borderColor: t.navyLine, borderRadius: 999, overflow: 'hidden' },
    segBox: { flexGrow: 1, flexShrink: 1, flexBasis: 'auto' },
    segDivider: { borderLeftWidth: 1, borderLeftColor: t.navyLine },
    seg: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6, paddingVertical: 9 },
    segText: { fontSize: 12.5, fontWeight: '700' },
    label: { fontFamily: DISPLAY_FONT, fontSize: 13, letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 8 },
    card: { padding: 12, gap: 10 },
    link: { fontSize: 13.5, fontWeight: '700', color: t.goldBright, paddingVertical: 4 },
    saveBar: { position: 'absolute', left: 12, right: 12, bottom: 12, flexDirection: 'row', alignItems: 'center', gap: 10 },
    msg: { fontSize: 13, fontWeight: '700' },
  }),
);
